/**
 * Comms — the Lanternfall channel, docked bottom-left of the hub.
 *
 * A town needs a noticeboard. This is the one place in STILLSUN where players who are not
 * in the same raid can hear each other, so it sits *beside* the briefing rather than
 * inside it: a dock over the shell, always one glance away, never a tab you have to leave
 * your loadout to visit.
 *
 * It mounts only while the hub is visible (App.tsx), and `chatFeed.stop()` on unmount is
 * what guarantees a raid never carries a polling timer into the frame loop.
 *
 * Keyboard: input.ts already hands a focused INPUT the keyboard outright (its keydown
 * handler returns early for INPUT/TEXTAREA/SELECT/contentEditable), so typing "wasd" here
 * cannot walk anybody anywhere. The one key that bypasses that check is Escape, which
 * input.ts reads before the early return — hence `stopPropagation()` on every key, exactly
 * as main.ts does for the in-raid chat box.
 */

import type { VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { chatFeed, MAX_CHAT_LEN, type ChatMessage, type ChatState } from '../../net/chat-api.js';
import { ROLE_BADGE } from '../../net/session.js';
import { Icon } from '../icons.js';
import './comms.css';

const OPEN_KEY = 'stillsun.comms.open';

/** Expanded on a desktop, a tab on a phone — until the player says otherwise. */
function defaultOpen(): boolean {
  try {
    const stored = localStorage.getItem(OPEN_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    /* private mode: fall through to the viewport's own answer */
  }
  // Height matters as much as width. An 844x390 landscape phone clears the 700px line, and the
  // expanded dock is ~230px of a 390px viewport — it buried the header, the rail and whatever
  // tab you were reading under the noticeboard. A short viewport gets the collapsed tab.
  return typeof matchMedia === 'function'
    ? matchMedia('(min-width: 700px) and (min-height: 560px)').matches
    : true;
}

function rememberOpen(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    /* private mode: the choice simply does not survive the reload */
  }
}

/** hh:mm, 24-hour. The date is never in question: this channel is minutes deep. */
function clock(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return '';
  const ms = ts < 1e11 ? ts * 1000 : ts;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function useChat(): ChatState {
  const [state, setState] = useState<ChatState>(chatFeed.current);
  useEffect(() => chatFeed.subscribe(setState), []);
  return state;
}

export interface CommsProps {
  token: string;
}

export function Comms(props: CommsProps): VNode {
  const chat = useChat();
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState('');
  const logRef = useRef<HTMLDivElement | null>(null);
  const columnRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // One poller for as long as the dock is up. The hub unmounts us when it hides, which is
  // what stops it — see App.tsx.
  useEffect(() => {
    chatFeed.start(props.token);
    return () => {
      chatFeed.stop();
    };
  }, [props.token]);

  /**
   * The newest line stays in view. Scrolling from a render effect is a race — the log is
   * still laying out (the panel grows with the conversation, and a long line wraps to two
   * rows on a phone), and a scroll against a stale height lands a row short. So the pin
   * hangs off a ResizeObserver on the message column instead: whenever the content grows,
   * we are already at the bottom. Unless the player scrolled up to read, in which case
   * they are reading, and yanking them back down would be rude.
   */
  useEffect(() => {
    const log = logRef.current;
    const column = columnRef.current;
    if (!open || !log || !column) return;
    const pin = (): void => {
      log.scrollTop = log.scrollHeight;
    };
    let nearBottom = true;
    const watchScroll = (): void => {
      nearBottom = log.scrollHeight - log.clientHeight - log.scrollTop < 48;
    };
    log.addEventListener('scroll', watchScroll, { passive: true });
    pin();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        log.removeEventListener('scroll', watchScroll);
      };
    }
    const grew = new ResizeObserver(() => {
      if (nearBottom) pin();
    });
    grew.observe(column);
    return () => {
      grew.disconnect();
      log.removeEventListener('scroll', watchScroll);
    };
  }, [open]);

  const submit = (): void => {
    const text = draft.trim();
    if (text.length === 0) return;
    setDraft('');
    void chatFeed.send(text).then((sent) => {
      // Rejected (rate limit, ban): hand the words back rather than eating them.
      if (!sent) setDraft(text);
    });
  };

  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    rememberOpen(next);
  };

  const online = String(chat.online);

  return (
    <section class={`comms${open ? ' on' : ''}`} aria-label="Lanternfall comms">
      <button
        type="button"
        class="comms-head"
        aria-expanded={open ? 'true' : 'false'}
        onClick={toggle}
      >
        <Icon name="chat" size={16} />
        <span class="comms-title">{open ? 'Lanternfall' : 'Comms'}</span>
        <span class="comms-online">
          · {online}
          {open ? ' online' : ''}
        </span>
        <span class="comms-caret" aria-hidden="true">
          {open ? '▾' : '▴'}
        </span>
      </button>

      {open ? (
        <div class="comms-body">
          <div
            class="comms-log"
            role="log"
            aria-live="polite"
            aria-label="Channel log"
            ref={logRef}
          >
            {/* One element for every line, so a ResizeObserver can watch the whole column
                grow rather than each message arriving. */}
            <div class="comms-column" ref={columnRef}>
              {chat.status === 'quiet' ? (
                <p class="comms-sys">The terminal is quiet — nobody is carrying the channel.</p>
              ) : null}
              {chat.status === 'cold' ? <p class="comms-sys">Opening the channel…</p> : null}
              {chat.status === 'live' && chat.messages.length === 0 ? (
                <p class="comms-sys">Nobody has spoken since the lamps were lit.</p>
              ) : null}
              {chat.messages.map((m) => (
                <Line key={m.id} message={m} />
              ))}
            </div>
          </div>

          {chat.notice.length > 0 ? <p class="comms-notice">{chat.notice}</p> : null}

          <form
            class="comms-row"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <label class="comms-label" for="comms-input">
              Message Lanternfall
            </label>
            <input
              id="comms-input"
              ref={inputRef}
              class="comms-input"
              type="text"
              autoComplete="off"
              maxLength={MAX_CHAT_LEN}
              placeholder={chat.status === 'quiet' ? 'the channel is down' : 'say something…'}
              disabled={chat.status === 'quiet'}
              value={draft}
              onInput={(e) => {
                setDraft((e.currentTarget as HTMLInputElement).value);
                chatFeed.clearNotice();
              }}
              onKeyDown={(e) => {
                // The game is listening on `window`; nothing typed here may reach it.
                e.stopPropagation();
                if (e.key === 'Escape') {
                  e.preventDefault();
                  inputRef.current?.blur();
                }
              }}
            />
            <button
              type="submit"
              class="comms-send"
              disabled={chat.sending || draft.trim().length === 0 || chat.status === 'quiet'}
            >
              Send
            </button>
          </form>
        </div>
      ) : null}
    </section>
  );
}

function Line(props: { message: ChatMessage }): VNode {
  const { message } = props;
  const badge = ROLE_BADGE[message.role];
  const time = clock(message.ts);
  return (
    <p class="comms-line">
      {time.length > 0 ? <span class="comms-time">{time}</span> : null}
      <span class="comms-name" style={badge ? { color: badge.color } : undefined}>
        {message.name}
      </span>
      {badge ? (
        <span class="comms-badge" style={{ color: badge.color, borderColor: badge.color }}>
          {badge.label}
        </span>
      ) : null}
      <span class="comms-text">{message.text}</span>
    </p>
  );
}
